import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Fade,
  FormControlLabel,
  Button,
  FormControl,
  FormLabel,
} from '@mui/material';

import { useHashLocation } from 'wouter/use-hash-location';
import { Redirect, useLocation, useRoute } from 'wouter';

import SaveIcon from '@mui/icons-material/Save';
import Checkbox from 'src/components/common/Checkbox';
import massActionUtils, {
  addSelectedCriteria,
  changeCriteriaValue,
  changeOperatorValue,
  removeCriteria,
} from 'src/components/massAction/state/utils';

import CriteriaMenu from './criteriaMenu/CriteriaMenu';
import CustomCard from 'src/components/common/Card';
import NameInput from './ActionNameInput';
import { useLoader } from 'src/reducers/loader';
import SelectedCriteriaList from './criteriaFields/SelectedCriteriaList';
import { COLUMS_SELECTOR } from '../MassActionEditor';
import {
  useSaveMassAction,
  useValidateMassActionName,
} from 'src/components/massAction/state/massAction';

const useStyles = makeStyles()(theme => ({
  title: { ...theme.typography.body2, color: theme.palette.text.secondary },
  cardHeader: { backgroundColor: theme.palette.grey[200], margin: '2px 2px 0 2px' },
  cardContent: { padding: '0', display: 'flex', flexDirection: 'column' },
  action: { marginTop: 0 },
  cardHeaderTitleRoot: { padding: '10px 15px' },
  appBar: {
    position: 'relative',
    textAlign: 'left',
  },
  flex: { flex: 1 },
  gridwrap1: {
    flexWrap: 'nowrap',
    overflow: 'hidden',
    flex: 1,
    border: theme.border.primary,
  },
  gridwrap2: {
    flex: 3,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: `1px solid ${theme.palette.grey[100]}`,
  },
  selectedCriteriasCardTitle: { fontSize: '1rem', color: theme.palette.text.secondary },
  selectedCriteriasCardHeader: { backgroundColor: theme.palette.grey[100] },
  selectedCriteriasCardContent: { padding: '0', display: 'flex', flexDirection: 'column', flex: 1 },
}));

const MassActionRequestConfig = ({ goToStep, ...otherProps }) => {
  const {
    currentName,
    currentReplay,
    currentByPass,
    selectedCriterias,
    currentMassActionId,
    // this data will not be migrated to form state (not used to POST/PUT a campaign)
    availableCriterias,
    isWritable,
    setMassActionState,
  } = otherProps;

  const selectionValidity = massActionUtils.selectionIsValid(selectedCriterias);

  const saveMassAction = useSaveMassAction();
  const validateMassActionName = useValidateMassActionName();
  const setLoader = useLoader();

  const onFieldChange = (fieldName, value) => {
    setMassActionState(state => ({
      ...state,
      [fieldName]: value,
    }));
  };

  const handleSelectedCriteriaChange = newSelectedCriterias => {
    onFieldChange('selectedCriterias', newSelectedCriterias);
  };

  const onAddCriteria = criteria => {
    handleSelectedCriteriaChange(addSelectedCriteria(criteria, { selectedCriterias }));
  };

  const onRemoveCriteria = ({ uniqueId, model, groupName, parent }) => {
    handleSelectedCriteriaChange(
      removeCriteria({ uniqueId, model, groupName, parent }, selectedCriterias)
    );
  };

  const onCriteriaValueChange = ({ uniqueId, model, groupName, parent, newValue }) => {
    handleSelectedCriteriaChange(
      changeCriteriaValue({ uniqueId, model, groupName, parent, newValue }, selectedCriterias)
    );
  };

  const onOperatorValueChange = ({ operatorValue, uniqueId, model, groupName, parent }) => {
    handleSelectedCriteriaChange(
      changeOperatorValue({ operatorValue, uniqueId, model, groupName, parent }, selectedCriterias)
    );
  };

  const { classes } = useStyles();
  const { t } = useTranslation();
  const [hLocation, hNavigate] = useHashLocation();
  const [location] = useLocation();
  const [afterCreateRouteMatch] = useRoute('/oneoff/create');

  if (afterCreateRouteMatch && hLocation.length > 1) {
    return <Redirect to={`/oneoff/${hLocation.replace('/', '')}/edit`} />;
  }

  if (!!currentMassActionId && location === '/oneoff/create' && hLocation === '/') {
    hNavigate(`${currentMassActionId}`);
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top AppBar */}
      <AppBar className={classes.appBar} color="primary">
        <Toolbar style={{ paddingLeft: '8px' }}>
          <div>
            <Typography variant="h5" color="inherit">
              {t(`smartmessaging.massAction.filtersSelection.title`)}
              {!!currentMassActionId && ` (${currentMassActionId})`}
            </Typography>
          </div>
          <div className={classes.flex} />
          {isWritable && (
            <div
              style={{
                marginLeft: '8px',
                marginRight: '8px',
                padding: '0 8px',
              }}
            >
              <Tooltip title={t('smartmessaging.massAction.save')}>
                <span>
                  <IconButton
                    aria-label="clear"
                    disabled={!selectionValidity.isValid}
                    color="inherit"
                    onClick={e => {
                      e.preventDefault();
                      setLoader(true);
                      saveMassAction().finally(() => setLoader(false));
                    }}
                    size="large"
                  >
                    <SaveIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}
        </Toolbar>
      </AppBar>
      {/* Name + Replay + Bypass + Filters + Next Step Button */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '16px 24px',
        }}
      >
        {/* Name + Replay + Bypass */}
        <div
          style={{
            display: 'flex',
          }}
        >
          <div
            style={{
              flex: 1,
            }}
          >
            <CustomCard
              title={t(`smartmessaging.campaignInfos.label.name`)}
              contentStyle={{
                display: 'flex',
                padding: 0,
              }}
            >
              <NameInput
                style={{
                  margin: '8px 0',
                  flex: 1,
                }}
                required
                disabled={!isWritable}
                placeholder={t('smartmessaging.campaignInfos.label.name')}
                value={currentName ?? ''}
                onChange={event => {
                  event.preventDefault();
                  onFieldChange('currentName', event.target.value);
                }}
              />
            </CustomCard>
          </div>
          <div
            style={{
              flex: 1,
              marginLeft: '8px',
            }}
          >
            <CustomCard
              title={t(`smartmessaging.campaignInfos.label.outOfHoursSms`)}
              contentStyle={{
                padding: '8px',
                paddingLeft: '0',
              }}
            >
              <div style={{ display: 'flex', textAlign: 'left' }}>
                <FormControlLabel
                  control={<Checkbox style={{ pointerEvents: 'auto' }} />}
                  style={{ pointerEvents: 'none', margin: 0 }}
                  checked={currentReplay}
                  onChange={() => {
                    setMassActionState(state => ({
                      ...state,
                      currentReplay: !currentReplay,
                      currentByPass: false,
                    }));
                  }}
                  label={
                    <Typography variant="body2" color="textSecondary">
                      {t('smartmessaging.campaignInfos.replaySms')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  control={<Checkbox style={{ pointerEvents: 'auto' }} />}
                  style={{ pointerEvents: 'none', margin: '0' }}
                  checked={currentByPass}
                  onChange={() => {
                    setMassActionState(state => ({
                      ...state,
                      currentByPass: !currentByPass,
                      currentReplay: false,
                    }));
                  }}
                  label={
                    <Typography variant="body2" color="textSecondary">
                      {t('smartmessaging.campaignInfos.bypassSmsHours')}
                    </Typography>
                  }
                />
              </div>
            </CustomCard>
          </div>
        </div>
        {/* Filters */}
        <Fade in timeout={500}>
          <Grid container direction="row" className={classes.gridwrap1}>
            {/* Available Filters */}
            <Grid
              style={{
                flexDirection: 'column',
                display: 'flex',
                width: 'fit-content',
                maxWidth: '360px',
              }}
            >
              <div
                style={{
                  overflow: 'auto',
                  display: 'flex',
                  flex: 1,
                  height: '100%',
                }}
              >
                <Card
                  elevation={0}
                  style={{
                    borderRadius: 0,
                    textAlign: 'left',
                    flexDirection: 'column',
                    display: 'flex',
                    flex: 1,
                  }}
                >
                  <CardHeader
                    className={classes.cardHeader}
                    classes={{
                      title: classes.title,
                      action: classes.action,
                      root: classes.cardHeaderTitleRoot,
                    }}
                    title={t('smartmessaging.massAction.availableFilters')}
                  />
                  <CardContent
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      paddingTop: 0,
                      flex: 1,
                      marginRight: '2px',
                      overflow: 'auto',
                    }}
                  >
                    {Object.keys(availableCriterias).map(groupName => (
                      <CriteriaMenu
                        key={groupName}
                        label={t(`smartmessaging.massAction.groupFilterBy.${groupName}`)}
                        availaibleCriterias={availableCriterias[groupName]}
                        isWritable={isWritable}
                        groupName={groupName}
                        selectedCriterias={selectedCriterias[groupName]}
                        onAddCriteria={onAddCriteria}
                      />
                    ))}
                  </CardContent>
                </Card>
              </div>
            </Grid>
            {/* Selected Filters */}
            <Grid className={classes.gridwrap2}>
              <div
                style={{
                  overflow: 'auto',
                  display: 'flex',
                  flex: 1,
                  height: '100%',
                }}
              >
                <Card
                  elevation={0}
                  style={{
                    minWidth: '600px',
                    flex: 1,
                    textAlign: 'left',
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <CardHeader
                    className={classes.cardHeader}
                    classes={{
                      title: classes.title,
                      action: classes.action,
                      root: classes.cardHeaderTitleRoot,
                    }}
                    title={t('smartmessaging.massAction.selectedFilters')}
                  />
                  <CardContent
                    style={{
                      overflow: 'auto',
                      flex: 1,
                      padding: '8px',
                    }}
                  >
                    {Object.keys(selectedCriterias).map(groupName => (
                      <Card style={{ overflow: 'visible' }} elevation={0} key={groupName}>
                        <FormControl style={{ display: 'block' }}>
                          <CardHeader
                            className={classes.selectedCriteriasCardHeader}
                            classes={{
                              title: classes.selectedCriteriasCardTitle,
                              action: classes.action,
                              root: classes.cardHeaderTitleRoot,
                            }}
                            title={
                              <FormLabel style={{ fontSize: '0.875rem' }}>
                                {t(`smartmessaging.massAction.groupFilterBy.${groupName}`)}
                              </FormLabel>
                            }
                          />
                          <CardContent className={classes.selectedCriteriasCardContent}>
                            <SelectedCriteriaList
                              groupName={groupName}
                              isWritable={isWritable}
                              criterias={selectedCriterias[groupName]}
                              selectionValidity={selectionValidity}
                              onCriteriaValueChange={onCriteriaValueChange}
                              onOperatorValueChange={onOperatorValueChange}
                              onRemoveCriteria={onRemoveCriteria}
                            />
                          </CardContent>
                        </FormControl>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </Grid>
          </Grid>
        </Fade>
        {/* Next step button */}
        <div style={{ textAlign: 'right', paddingTop: '12px' }}>
          <Button
            style={{ margin: '2px' }}
            variant="contained"
            color="primary"
            disabled={!selectionValidity.isValid}
            onClick={e => {
              e.preventDefault();
              validateMassActionName(() => goToStep(COLUMS_SELECTOR));
            }}
          >
            {t('smartmessaging.buttonLabel.next')}
          </Button>
        </div>
      </div>
    </div>
  );
};

MassActionRequestConfig.propTypes = {
  goToStep: PropTypes.func.isRequired,
};

export default MassActionRequestConfig;
